import { ldb, state } from '@codesuma/baseline'
import { ICountrySubscription } from './country-selection'
import { J_MONTHS, J_MONTHS_IRANIAN, J_MONTHS_AFGHAN } from '../configs/names'

// User display settings, persisted in localStorage and broadcast through the
// baseline state bus: subscribe with state.on('settings', handler) to react.

export type CalendarSystem = 'jalaali' | 'gregorian'
// How a solar year is written: the Shahanshahi year with the Jalaali one in
// brackets, the Shahanshahi year alone, or the Jalaali year alone.
export type YearFormat = 'both' | 'shahanshahi' | 'jalaali'
// Which names the Jalaali months go by: فروردین… or حمل…
export type MonthNames = 'iranian' | 'afghan'
// Five surfaces. Two carry the seasonal panorama — the day painting and the
// night one — and their pages take that painting's own sky as their paper, so
// the picture and the page are one colour. Two are the same calendar with no
// picture at all, pale and dark. The fifth is the month's own seasonal color.
export type Theme = 'light' | 'dark' | 'paper' | 'slate' | 'seasons'

export interface ISettings {
    system: CalendarSystem // primary calendar of the month grid
    yearFormat: YearFormat // how every solar year is written (helpers/year)
    monthNames: MonthNames // what configs/names J_MONTHS holds
    showSecondary: boolean // the other solar calendar inside day cells
    showHijri: boolean // hijri day inside day cells
    weekStart: number // Gregorian grid only; Persian weekday index: 0 = Saturday … 6 = Friday
    vertical: boolean // transposed grid: weekday labels down the right, weeks as columns
    showPhoneEvents: boolean // events read from the phone's calendar; native only
    phoneCalendars: string[] | null // ids of the phone calendars read; null reads them all
    showPhoneReminders: boolean // tasks read from the Reminders app; iPhone only
    phoneReminderLists: string[] | null // ids of the Reminders lists read; null reads them all
    remindersOff: boolean // reminders held back here, whatever the platform allows
    theme: Theme // what the calendar sits on, and every color that has to read on it
    // Countries whose holidays the user added, each with the regions they live
    // in and the occasions they switched off. See services/country-selection.
    countries: ICountrySubscription[]
}

const DEFAULTS: ISettings = {
    system: 'jalaali',
    yearFormat: 'both',
    monthNames: 'iranian',
    showSecondary: true,
    showHijri: false,
    // Sunday, the week the Gregorian grid is normally read in. Only that grid
    // uses this — a Jalaali month always opens on شنبه — and a saved choice
    // from the settings sheet wins over it.
    weekStart: 1,
    vertical: false,
    // Off until it is asked for: turning it on is what prompts the phone for
    // calendar permission, and a calendar app should not ask on first launch.
    showPhoneEvents: false,
    // Which of the phone's calendars are read. Null is an install that
    // connected before there was a choice, and it goes on reading every
    // calendar as it always did. A new connection starts from an empty list —
    // see phoneCalendar.enable — so the person picks, rather than every
    // calendar on the phone arriving at once.
    phoneCalendars: null,
    // Off until asked for, like the calendar above, and for the same reason.
    // Every list is read until some are unticked: people keep a few lists of
    // their own there, not the tens of subscribed calendars a phone can hold.
    showPhoneReminders: false,
    phoneReminderLists: null,
    // Reminders are on: one only exists because the user asked for it. The
    // switch is the way back out without editing every occasion and task.
    remindersOff: false,
    // The day panorama is what the app opens on. The phone's dark mode does not
    // decide it — the night panorama and the colored theme are a step away,
    // behind the theme button, and the choice sticks once it is made.
    theme: 'light',
    // Nobody's holidays but Iran's until the user asks for them. The built-in
    // lists already cover the calendar this app is for.
    countries: [],
}

const KEY = 'settings'
const current: ISettings = { ...DEFAULTS, ...(ldb.get(KEY) || {}) }

// Before anything is drawn, and again before the broadcast, so the pages that
// re-render on it already read the new names.
const applyMonthNames = () =>
    J_MONTHS.splice(0, 12, ...(current.monthNames === 'afghan' ? J_MONTHS_AFGHAN : J_MONTHS_IRANIAN))
applyMonthNames()

function get(): ISettings {
    return { ...current }
}

function update(patch: Partial<ISettings>) {
    Object.assign(current, patch)
    applyMonthNames()
    ldb.set(KEY, current)
    state.set('settings', { ...current })
}

export default { get, update }
